import { boot } from '@dazn/bootstrap-lib';

import { getChapters, AllChaptersConfig } from './chapters';

type ChapterName = boot.config.ChapterName;

export interface FeConfigCountries {
    native: string[];
    disabled: string[];
    mapped: { [K: string]: string };
}

export const SafeModeSource = {
    GCP: 'GCP',
    GCPFireDrill: 'GCP_FIREDRILL',
} as const;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type SafeModeSource = boot.util.Values<typeof SafeModeSource>;

export interface SafeModeConfig {
    /**
     * Url to lite version of dazn.com
     */
    homePageURL: string;
    /**
     * Url for fetching the status of lite.dazn.com (fireball)
     */
    statusURL: string;
    /**
     * Source of safe mode status: GCP or GCPFireDrill
     */
    source: SafeModeSource;
    /**
     * Base64 with qrcode image with url
     */
    homePageQRBase64: string;
}

/**
 * Module responsible for Configuration & Constants.
 The only argument for the `create` method has a predefined schema {@link ../_nodejs/config/schemas/config.schema.ts} and should always use output of the build function available in this package.
 */
export interface ConfigurationModuleParams {
    /**
     * Storage key for language
     */
    UILanguageKey: string;
    /**
     * Storage key for token (same as Massive is using)
     */
    tokenStorageKey: string;
    /**
     * Window object key (`dazn`)
     */
    globalNamespaceKey: string;
    rootAuthorizedChapter: ChapterName;
    unauthorizedChapter: ChapterName;
    loginChapter: ChapterName;
    onLogoutChapter: ChapterName;
    defaultActiveUserTypes: string[];
    resourceStringLinkOverrides: {
        [Country: string]: {
            [Lang: string]: {
                [K: string]: string;
            };
        };
    };
    chapters: AllChaptersConfig;
    /**
     * Url of downloading resources
     */
    resourcesBaseUrl: string;
    features: Record<string, boolean>;
    /**
     * Could be one of `testing`, `staging`, `production`
     */
    environment: boot.config.Environment;
    /**
     * Name of the target
     */
    target: string;
    /**
     * Define group of environment (eg. `beta`)
     */
    environmentGroup: string;
    safeMode: Record<
        boot.brand.BrandName,
        {
            GCP: SafeModeConfig;
            GCPFireDrill: SafeModeConfig;
        }
    >;
    countries: FeConfigCountries;
    defaultAnonymousChapters: boot.config.CountryToChapterMap;
}

export function createConfigurationModule({
    countries,
    defaultAnonymousChapters,
    resourcesBaseUrl,
    features,
    resourceStringLinkOverrides,

    // target
    target,
    environmentGroup,

    // special case
    chapters,

    // hardcoded in this module
    UILanguageKey,
    tokenStorageKey,
    globalNamespaceKey,

    rootAuthorizedChapter,
    unauthorizedChapter,
    loginChapter,
    onLogoutChapter,

    // hardcoded in this module - ENV SPECIFIC
    environment,
}: ConfigurationModuleParams) {
    const chaptersAPI = getChapters(
        chapters,
        rootAuthorizedChapter,
        unauthorizedChapter,
        loginChapter,
        onLogoutChapter,
    );
    chaptersAPI.applyRuntimeConfiguration(defaultAnonymousChapters);

    return {
        // hardcoded here
        UILanguageKey,
        tokenStorageKey,
        globalNamespaceKey,

        // target buildtime
        environment,
        environmentGroup,
        target,
        resourcesBaseUrl,
        features,
        resourceStringLinkOverrides,

        chapters: chaptersAPI,
        countries,
        appUrlPrefix: target === 'web' ? '' : `/app/${target}`,
    };
}
